import { parseUrl, getCleanUrl, getLang, getUrlWithTrailingSlash } from '@theme/js/utils/url.util';
import { languageService } from '@theme/js/services/language.service';
import { SEO_LINK_TYPE } from '@core/js/components/system/Page/constants';

// todo SEO links for bonuses - (bonusDeepLinkingUrl needs to be correct for non-default languages, BE and CMS changes needed)

/**
 * @param {Object} props
 * @param {Object} props.page
 * @param {string[]} props.allowedLocales
 * @param {string} props.rootPage
 * @param {string} props.defaultLocale
 * @param {boolean} props.useTrailingSlash
 * @param {SEO_LINK_TYPE} [props.seoLinkType]
 * @returns {{ href: string; hrefLang: string }[]}
 */
export const getAlternateLinks = ({ page, allowedLocales, defaultLocale, rootPage, useTrailingSlash, seoLinkType }) => { /* Lugano changes */
    if (!allowedLocales || seoLinkType === SEO_LINK_TYPE.DISABLED || !page) {
        return [];
    }

    const { localeToUrl = {} } = page.layoutDynamicConfig.canonicalTagConfiguration || {};
    const alternateLinks = [];
    const getDynamicSegment = () => {
        const currentLang = getLang();
        const currentCleanPath = getCleanUrl(location.pathname);
        const pageConfigPath = getCleanUrl(page.url?.[currentLang] || page.url?.[defaultLocale]);

        if (!currentCleanPath.startsWith(pageConfigPath)) {
            return '';
        }

        return currentCleanPath.slice(pageConfigPath.length);
    };

    /* Lugano changes */
    const getRootPageLocale = ({ lang }) => {
        const cleanPagePath = getCleanUrl(page.url[lang]);
        const cleanCurrentPath = getCleanUrl(location.pathname);

        return cleanPagePath === rootPage && cleanPagePath === cleanCurrentPath ? cleanPagePath : '/';
    };

    for (const lang of allowedLocales) {
        if (lang in localeToUrl) {
            continue;
        }

        const pathLang = languageService.getPathLang(lang) || languageService.getTwoLetterLanguage(lang);

        const pathname = (() => {
            const pagePath = page.url?.[lang] || page.url?.[defaultLocale];

            if (seoLinkType === SEO_LINK_TYPE.CURRENT_PATH || getCleanUrl(location.pathname) === '/' || !pagePath) {
                return getCleanUrl(location.pathname);
            }

            const isRootPage = page.url?.[lang] && getCleanUrl(page.url?.[lang]) === rootPage;

            if (isRootPage) {
                return getRootPageLocale({ lang });
            }

            if (seoLinkType === SEO_LINK_TYPE.WITH_DYNAMIC_SEGMENT) {
                return getCleanUrl(pagePath) + getDynamicSegment();
            }

            return pagePath;
        })();

        const alternateLink = `${location.origin}${pathLang ? `/${pathLang}` : ''}${getCleanUrl(pathname)}`;
        const href = getUrlWithTrailingSlash(alternateLink, useTrailingSlash);

        alternateLinks.push({ href, hrefLang: lang.replace('_', '-').replace(/..$/, 'CH') });
    }
    /* Lugano changes */

    return alternateLinks;
};

/**
 * @param {Object} props
 * @param {Object} props.page
 * @param {string} props.defaultLocale
 * @param {string} [props.lang]
 * @param {boolean} props.useTrailingSlash
 * @param {SEO_LINK_TYPE} [props.seoLinkType]
 * @returns {string}
 */
export const getCanonicalLinkUrl = ({ page, defaultLocale, lang, useTrailingSlash, seoLinkType }) => {
    if (seoLinkType === SEO_LINK_TYPE.DISABLED) {
        return '';
    }

    const { useLayout, layout, customUrl } = page?.layoutDynamicConfig.canonicalTagConfiguration || {};
    const url = (() => {
        if (seoLinkType === SEO_LINK_TYPE.CURRENT_PATH) {
            return location.origin + location.pathname;
        } else if (useLayout) {
            return location.origin + layout;
        } else if (!customUrl) {
            return location.origin + location.pathname;
        }

        return customUrl;
    })();

    const language = languageService.getTwoLetterLanguage(lang);
    const defaultLanguage = languageService.getTwoLetterLanguage(defaultLocale);
    let linkUrl = new window.URL(url, document.baseURI);

    /* Lugano changes */
    const { cleanUrl } = parseUrl(linkUrl.pathname);

    if (useLayout) {
        linkUrl = language === defaultLanguage || !language ? `${linkUrl.origin}${cleanUrl}` : `${linkUrl.origin}/${language}${cleanUrl}`;
    } else {
        const hasPageNameEnding = linkUrl.pathname.endsWith(`${cleanUrl}/`);
        const hasLanguageEnding = linkUrl.pathname.endsWith(`/${language}`);

        if (hasPageNameEnding) {
            linkUrl = linkUrl.toString().replace(/.$/, '');
        } else if (hasLanguageEnding) {
            linkUrl = `${linkUrl}/`;
        }
    }
    /* Lugano changes */

    return getUrlWithTrailingSlash(linkUrl.toString(), useTrailingSlash);
};

export const getPageStyles = ({ page }) => {
    const {
        customCSS = '',
        layoutDynamicConfig: {
            backgroundConfiguration: {
                backgroundImage,
                backgroundColor,
                backgroundFixed
            }
        }
    } = page;

    const bodyStyles = `body {
        background-image: url("${backgroundImage}");
        ${backgroundColor ? `background-color: ${backgroundColor};` : ''}
        ${backgroundFixed ? 'background-attachment: fixed;' : ''}
    }`;

    return `${bodyStyles} ${customCSS}`;
};

export const getRootClassNames = (rootClassNames) => rootClassNames ? rootClassNames.split(' ').map(className => `root-${className}`) : [];

export const getPageClassNames = (rootClassNames, deviceClassNames = '') => {
    const deviceClassNamesArray = deviceClassNames.split(' ');
    let pageClassNames = Array
        .from(document.documentElement.classList)
        .filter(className => !/^root-/.test(className) && className !== 'portrait' && className !== 'landscape');

    deviceClassNamesArray.forEach((deviceClassName) => {
        pageClassNames = pageClassNames.filter((pageClassName) => pageClassName !== deviceClassName);
    });

    const rootClassNamesArray = getRootClassNames(rootClassNames);

    pageClassNames = [
        ...pageClassNames,
        ...deviceClassNamesArray,
        ...rootClassNamesArray
    ];

    return pageClassNames.join(' ');
};
